// components/Skills.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useMobileDetection } from '../hooks/useMobileDetection';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const AnimatedSkillItem = ({ children }) => {
    const { isMobile } = useMobileDetection();
    const [isHovering, setIsHovering] = useState(false);
    const [isTouching, setIsTouching] = useState(false);

    const directions = useMemo(() => {
        return Array.from({ length: children.length }, () => Math.random() > 0.5 ? 1 : -1);
    }, [children.length]);

    const handleTouchStart = () => {
        setIsTouching(true);
        setTimeout(() => setIsTouching(false), 1000);
    };

    const isActive = isMobile ? isTouching : isHovering;

    return (
        <li
            className="text-lg md:text-xl text-black tracking-tight font-normal cursor-pointer select-none"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            onMouseEnter={() => !isMobile && setIsHovering(true)}
            onMouseLeave={() => !isMobile && setIsHovering(false)}
            onTouchStart={isMobile ? handleTouchStart : undefined}
        >
            <span className="inline-block" style={{ overflow: 'hidden', height: '1.2em', position: 'relative' }}>
                {children.split('').map((letter, index) => {
                    const direction = directions[index];
                    const delay = index * (isMobile ? 15 : 20);

                    return (
                        <span
                            key={index}
                            className="inline-block relative"
                            style={{ overflow: 'hidden', height: '1.2em', position: 'relative', verticalAlign: 'top' }}
                        >
                            <span
                                className="block transition-transform ease-in-out"
                                style={{
                                    transform: isActive ?
                                        `translateY(${direction * 100}%)`
                                        : 'translateY(0)',
                                    transitionDuration: isMobile ? '250ms' : '350ms',
                                    transitionDelay: `${delay}ms`,
                                    lineHeight: '1.2em',
                                    height: '1.2em'
                                }}
                            >
                                {letter === ' ' ? ' ' : letter}
                            </span>
                            <span
                                className="block absolute top-0 left-0 transition-transform ease-in-out"
                                style={{
                                    transform: isActive ?
                                        'translateY(0)'
                                        : `translateY(${-direction * 100}%)`,
                                    transitionDuration: isMobile ? '250ms' : '350ms',
                                    transitionDelay: `${delay}ms`,
                                    lineHeight: '1.2em',
                                    height: '1.2em'
                                }}
                            >
                                {letter === ' ' ? ' ' : letter}
                            </span>
                        </span>
                    );
                })}
            </span>
        </li>
    );
};

const SkillGroup = ({ title, items }) => (
    <div>
        <p className="text-xs font-mono text-gray-400 tracking-widest uppercase mb-4">
            {title}
        </p>
        <ul className="space-y-3 md:space-y-4">
            {items.map((skill, index) => (
                <AnimatedSkillItem key={index}>
                    {skill}
                </AnimatedSkillItem>
            ))}
        </ul>
    </div>
);

const Skills = () => {
    const { isMobile } = useMobileDetection();
    const { language } = useLanguage();
    const t = useTranslation(language);
    const [isSticky, setIsSticky] = useState(false);
    const [headerWidth, setHeaderWidth] = useState(null);
    const sectionRef = useRef(null);
    const headerRef = useRef(null);
    const headerWrapperRef = useRef(null);
    const headerInitialPos = useRef(null);

    const groups = [
        { title: t('skillsGroupBroadcast'), items: t('skillsBroadcastItems') },
        { title: t('skillsGroupDev'), items: t('skillsDevItems') },
        { title: t('skillsGroupTeaching'), items: t('skillsTeachingItems') },
    ];

    useEffect(() => {
        // Skip sticky logic on mobile
        if (isMobile) return;

        // Get and save initial position and width after first render
        if (headerRef.current && headerWrapperRef.current && !headerInitialPos.current) {
            const rect = headerRef.current.getBoundingClientRect();
            const wrapperRect = headerWrapperRef.current.getBoundingClientRect();

            headerInitialPos.current = {
                top: rect.top + window.scrollY,
                height: rect.height
            };

            // Store the exact width to prevent text reflow when sticky
            setHeaderWidth(wrapperRect.width);
        }

        const handleScroll = () => {
            if (!sectionRef.current || !headerRef.current || !headerInitialPos.current) return;

            const scrollY = window.scrollY;
            const sectionRect = sectionRef.current.getBoundingClientRect();
            const headerInitialTop = headerInitialPos.current.top;

            // Space from the top of the viewport where we want the header to stick
            const stickyOffset = 96;

            // Check if we're within the section bounds
            const isScrolledToSection = scrollY >= headerInitialTop - stickyOffset;
            const isStillWithinSection = sectionRect.bottom > headerInitialPos.current.height + stickyOffset;

            setIsSticky(isScrolledToSection && isStillWithinSection);
        };

        const handleResize = () => {
            // Recalculate width on window resize
            if (headerWrapperRef.current && !isSticky) {
                const wrapperRect = headerWrapperRef.current.getBoundingClientRect();
                setHeaderWidth(wrapperRect.width);
            }
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);
        handleScroll(); // Initial check

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isMobile, isSticky]);

    return (
        <section ref={sectionRef} id="skills" className="w-full bg-white py-16 md:py-24 rounded-b-[40px]">
            <div className="container mx-auto px-4 md:px-8 lg:px-32">
                <div className="flex flex-col md:flex-row justify-center max-w-6xl mx-auto">
                    {/* Header wrapper */}
                    <div ref={headerWrapperRef} className="w-full md:w-1/3 mb-8 md:mb-0">
                        <div
                            ref={headerRef}
                            className={`${!isMobile && isSticky ? 'md:fixed top-24' : ''}`}
                            style={{
                                width: !isMobile && isSticky && headerWidth ? `${headerWidth}px` : 'inherit'
                            }}
                        >
                            <h2 className="text-4xl md:text-5xl text-black mb-4 md:mb-6 tracking-tight"
                                style={{ fontFamily: "'Space Grotesk', 'Archivo', sans-serif" }}>
                                {t('skillsTitle')}
                            </h2>
                            <p className="text-base md:text-lg text-gray-700"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                {t('skillsSubtitle')}
                            </p>
                        </div>
                    </div>

                    {/* Groups section */}
                    <div className="w-full md:w-2/3 md:pl-16 grid grid-cols-1 sm:grid-cols-3 gap-10 md:gap-8">
                        {groups.map((group) => (
                            <SkillGroup key={group.title} title={group.title} items={group.items} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Skills;
